import isEqual from 'lodash/isEqual';
import { useCallback, useEffect, useState } from 'react';

function useLocalStorage(key, initialValue, compareFunction) {
  // Get from local storage then
  // parse stored json or return initialValue
  const readValue = useCallback((initValue) => {
    // Prevent build error "window is undefined" but keep keep working
    if (typeof window === 'undefined') {
      return initValue;
    }

    try {
      const item = window.localStorage.getItem(key);
      if (item && compareFunction) {
        return compareFunction(JSON.parse(item), initValue);
      }
      return item ? JSON.parse(item) : initValue;
    } catch (error) {
      console.warn(`Error reading localStorage key “${key}”:`, error);
      return initValue;
    }
  }, []);

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(readValue(initialValue));

  useEffect(() => {
    setStoredValue((value) =>
      isEqual(value, readValue(initialValue)) ? value : readValue(initialValue)
    );
  }, [initialValue]);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value) => {
    // Prevent build error "window is undefined" but keeps working
    if (typeof window == 'undefined') {
      console.warn(
        `Tried setting localStorage key “${key}” even though environment is not a client`
      );
    }

    try {
      // Allow value to be a function so we have the same API as useState
      const newValue = value instanceof Function ? value(storedValue) : value;

      // Save to local storage
      window.localStorage.setItem(key, JSON.stringify(newValue));

      // Save state
      setStoredValue(newValue);

      // We dispatch a custom event so every useLocalStorage hook are notified
      window.dispatchEvent(new Event('local-storage'));
    } catch (error) {
      console.warn(`Error setting localStorage key “${key}”:`, error);
    }
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setStoredValue(readValue(initialValue));
    };

    // this only works for other documents, not the current one
    window.addEventListener('storage', handleStorageChange);

    // this is a custom event, triggered in writeValueToLocalStorage
    window.addEventListener('local-storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-storage', handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  return [storedValue, setValue];
}

export default useLocalStorage;
